
import { useTranslation } from 'react-i18next';

export default function LessonExamples() {
  const { t } = useTranslation();

  const examples = [
    {
      title: 'スケートパークでの会話',
      scenario: 'アメリカのスケートパークで現地ライダーと話す',
      dialogue: [
        { speaker: 'You', text: 'Hey, mind if I drop in after you?' },
        { speaker: 'Local', text: 'Sure thing! That was a sick kickflip to fakie!' },
        { speaker: 'You', text: 'Thanks! I\'ve been working on that trick for weeks.' },
        { speaker: 'Local', text: 'It shows! Want to session this bowl together?' }
      ],
      vocabulary: ['drop in', 'sick', 'kickflip to fakie', 'session'],
      image: 'https://public.readdy.ai/ai/img_res/00d099caa909716f6db4c14306b8fd3d.jpg'
    },
    {
      title: 'BMXトリックの説明',
      scenario: 'YouTubeでトリック動画を投稿する時の英語',
      dialogue: [
        { speaker: 'Video', text: 'What\'s up everyone! Today I\'m gonna show you how to do a tailwhip.' },
        { speaker: 'Video', text: 'First, you need to get comfortable with your bunny hop.' },
        { speaker: 'Video', text: 'Then, kick the frame around while keeping your hands on the bars.' },
        { speaker: 'Video', text: 'Catch it with your feet and ride away clean!' }
      ],
      vocabulary: ['tailwhip', 'bunny hop', 'kick the frame', 'ride away clean'],
      image: 'https://readdy.ai/api/search-image?query=Young%20BMX%20rider%20recording%20tutorial%20video%20with%20smartphone%20on%20tripod%2C%20demonstrating%20tailwhip%20technique%20step%20by%20step%2C%20modern%20skate%20park%20setting%20with%20concrete%20ramps%2C%20rider%20wearing%20protective%20gear%20and%20casual%20clothes%2C%20educational%20content%20creation%20setup%20with%20good%20lighting%20for%20video%20recording&width=400&height=300&seq=lesson2-new&orientation=landscape'
    },
    {
      title: '海外旅行での注文',
      scenario: 'カリフォルニアのスケートショップで買い物',
      dialogue: [
        { speaker: 'You', text: 'Excuse me, do you have any 8.25 decks in stock?' },
        { speaker: 'Staff', text: 'Yeah, we\'ve got some fresh ones right here. What kind of setup are you looking for?' },
        { speaker: 'You', text: 'I need some new trucks and wheels too. Something good for street skating.' },
        { speaker: 'Staff', text: 'I\'d recommend these Independent trucks and some 52mm wheels.' }
      ],
      vocabulary: ['deck', 'in stock', 'setup', 'trucks', 'street skating'],
      image: 'https://readdy.ai/api/search-image?query=Customer%20and%20friendly%20shop%20staff%20discussing%20skateboard%20equipment%20in%20bright%20modern%20skate%20shop%2C%20walls%20lined%20with%20colorful%20skateboard%20decks%2C%20customer%20pointing%20at%20specific%20board%20while%20staff%20member%20explains%20features%2C%20California%20surf%20and%20skate%20culture%20atmosphere%2C%20natural%20shopping%20interaction%20with%20skateboard%20gear%20displayed&width=400&height=300&seq=lesson3-new&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            レッスン例
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            実際のレッスンではこんな場面を想定した実践的な英語を学びます
          </p>
        </div>

        <div className="space-y-12">
          {examples.map((example, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="lg:flex">
                <div className="lg:w-1/3">
                  <img
                    src={example.image}
                    alt={example.title}
                    className="w-full h-64 lg:h-full object-cover object-top"
                  />
                </div>
                <div className="lg:w-2/3 p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {example.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {example.scenario}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      会話例：
                    </h4>
                    <div className="space-y-3">
                      {example.dialogue.map((line, lineIndex) => (
                        <div key={lineIndex} className="flex">
                          <span className="font-semibold text-teal-600 w-16 flex-shrink-0">
                            {line.speaker}:
                          </span>
                          <span className="text-gray-700">
                            {line.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      学習する単語・表現：
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {example.vocabulary.map((word, wordIndex) => (
                        <span
                          key={wordIndex}
                          className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-medium"
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              レッスンの流れ
            </h3>
            <div className="grid md:grid-cols-4 gap-6 mt-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">ウォームアップ</h4>
                <p className="text-gray-600 text-sm">前回の復習と今日のトピック紹介</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">シチュエーション学習</h4>
                <p className="text-gray-600 text-sm">実際の場面を想定した会話練習</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">単語・表現学習</h4>
                <p className="text-gray-600 text-sm">スケート・BMX用語の習得</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold">4</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">実践練習</h4>
                <p className="text-gray-600 text-sm">学んだ内容を使った自由会話</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <button className="bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
            無料体験レッスンを予約する
          </button>
        </div>
      </div>
    </section>
  );
}
